import React from 'react';
import { SignUpFormData } from '../../../types/auth';
import { isPasswordValid } from '../../../utils/passwordPolicy';
import PasswordRequirements from './PasswordRequirements';
import SignUpPlanPicker from './SignUpPlanPicker';
import TurnstileWidget from '../TurnstileWidget';
import { isSandboxBuild } from '../../../utils/sandbox';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

interface SignUpFormProps {
  signupData: SignUpFormData;
  handleSignupChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleSignup: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent) => void;
  setView: (view: string) => void;
  error: string | null;
  isLoading: boolean;
  /** Demo sandbox: receives the Turnstile token (null when it expires or is reset). */
  onCaptchaToken?: (token: string | null) => void;
  /** Demo sandbox: change to get a fresh Turnstile token after an attempt (tokens are single-use). */
  captchaResetKey?: number;
  /** Demo sandbox: whether a Turnstile token is ready. */
  hasCaptchaToken?: boolean;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  signupData,
  handleSignupChange,
  handleSignup,
  setView,
  error,
  isLoading,
  onCaptchaToken,
  captchaResetKey = 0,
  hasCaptchaToken = false
}) => {
  // Demo sandbox (demo-sandbox plan §4.1): Core with sample data, fictional-data consent, Turnstile
  const sandbox = isSandboxBuild();
  const sandboxReady = !sandbox || (Boolean(signupData.fictionalDataConsent) && hasCaptchaToken);
  const passwordHasError = Boolean(signupData.password) && !isPasswordValid(signupData.password);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    handleSignup(e);
  };

  return (
    <div className="w-full max-w-sm py-8 md:py-10">
      <h2 className="text-2xl font-semibold mb-1 text-slate-800 text-left">Create your account</h2>
      <p className="text-gray-500 mb-2 text-sm text-left">Enter your information to set up your organization</p>
      {/* Phase 13 (ACC-08): every agency starts on a 14-day trial of the plan picked below (Core by default); it can be changed during the trial */}
      <p className="mb-6 text-sm font-medium text-left text-teal-800" data-testid="signup-trial-note">
        {sandbox
          ? 'Try Pelora free for 14 days: no card needed, sample agency data included'
          : 'Start your 14-day free trial — no card needed'}
      </p>
      
      {error && <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm">{error}</div>}
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-medium text-slate-800 text-sm text-left" htmlFor="firstName">First name</label>
            <input
              className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              type="text"
              id="firstName"
              name="firstName"
              placeholder="First name"
              value={signupData.firstName}
              onChange={handleSignupChange}
            />
          </div>
          
          <div className="flex flex-col gap-2">
            <label className="font-medium text-slate-800 text-sm text-left" htmlFor="lastName">Last name</label>
            <input
              className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              type="text"
              id="lastName"
              name="lastName"
              placeholder="Last name"
              value={signupData.lastName}
              onChange={handleSignupChange}
            />
          </div>
        </div>
          <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="orgName">Organization name</label>
          <input
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            type="text"
            id="orgName"
            name="organizationName"
            placeholder="Your organization name"
            value={signupData.organizationName}
            onChange={handleSignupChange}
          />
        </div>
        {!sandbox && <SignUpPlanPicker value={signupData.plan} onChange={handleSignupChange} />}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="state">State of operation <span className="font-normal text-slate-500">(optional)</span></label>
          <select
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-white"
            id="state"
            name="state"
            value={signupData.state}
            onChange={handleSignupChange}
          >
            <option value="">Select your state</option>
            {US_STATES.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="email">Email address</label>
          <input
            className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
            type="email"
            id="email"
            name="email"
            placeholder="name@practice.com"
            value={signupData.email}
            onChange={handleSignupChange}
          />
          <p className="text-xs text-gray-500">We'll send your account verification to this email</p>
        </div>          <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="password">Password</label>
          <input
            className={`w-full p-3 border rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
              passwordHasError && signupData.password ? 'border-red-300' : 'border-slate-200'
            }`}
            type="password"
            id="password"
            name="password"
            placeholder="Create a strong password"
            value={signupData.password}
            onChange={handleSignupChange}
          />
          <PasswordRequirements password={signupData.password} />
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium text-slate-800 text-sm text-left" htmlFor="confirmPassword">Confirm password</label>
          <input
            className={`w-full p-3 border rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 ${
              signupData.password && signupData.confirmPassword && signupData.password !== signupData.confirmPassword 
                ? 'border-red-300' 
                : 'border-slate-200'
            }`}
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            placeholder="Confirm your password"
            value={signupData.confirmPassword || ''}
            onChange={handleSignupChange}
          />
          {signupData.password && signupData.confirmPassword && signupData.password !== signupData.confirmPassword && (
            <div className="text-xs text-red-500">
              Passwords don't match
            </div>
          )}
        </div>
        
        <div className="mb-2 mt-1">
          <label className="inline-flex items-start">
            <input
              type="checkbox"
              name="acceptTerms"
              checked={signupData.acceptTerms}
              onChange={handleSignupChange}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-20 mt-1"
            />
            <span className="ml-2 text-sm text-gray-600">
              I agree to the <a href="https://www.pelorahealth.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Terms of Service</a> and <a href="https://www.pelorahealth.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</a>
            </span>
          </label>
        </div>
        
        {sandbox && (
          <div className="flex flex-col gap-3" data-testid="signup-sandbox">
            <label className="inline-flex items-start">
              <input
                type="checkbox"
                name="fictionalDataConsent"
                checked={Boolean(signupData.fictionalDataConsent)}
                onChange={handleSignupChange}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-20 mt-1"
              />
              <span className="ml-2 text-sm text-gray-600">
                I will only enter fictional information. This demo sandbox is not for real patient data and is deleted after 21 days.
              </span>
            </label>
            <TurnstileWidget onToken={(token) => onCaptchaToken?.(token)} resetKey={captchaResetKey} />
          </div>
        )}

        <button 
          type="submit"
          className={`w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-400 disabled:hover:bg-slate-400 disabled:opacity-70 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          disabled={isLoading || !signupData.acceptTerms || !sandboxReady}
        >
          {isLoading ? 'Creating account...' : 'Create account'}
        </button>
      </form>
      
      <div className="mt-6 text-center text-sm">
        <p>
          Already have an account? {' '}
          <button 
            type="button"
            onClick={() => setView('signin')} 
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignUpForm;
